/**
 * Pulsante di segnalazione, sempre a portata.
 *
 * Il valore non sta nel campo di testo ma in ciò che si allega DA SOLO:
 * pagina, versione del gestionale, browser. "A volte clicco e non si apre" è
 * inutilizzabile; la stessa frase con schermata e versione diventa
 * diagnosticabile. Chiedere questi dati all'utente non funziona — nessuno sa
 * quale versione sta usando — quindi si registrano senza domandare.
 */
import { useState } from 'react';
import { segnalazioniApi } from '../api';
import Modal from '../components/Modal';

// Solo il nome del browser: la stringa completa è illeggibile e non serve.
function browserBreve() {
  const ua = navigator.userAgent || '';
  const nome = /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : /Firefox\//.test(ua) ? 'Firefox' : 'altro';
  const so = /Macintosh/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows'
    : /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : '';
  return [nome, so].filter(Boolean).join(' su ');
}

const vuota = () => ({ tipo: 'bug', titolo: '', descrizione: '' });

export default function PulsanteSegnala() {
  const [aperto, setAperto] = useState(false);
  const [form, setForm] = useState(vuota());
  const [inviando, setInviando] = useState(false);
  const [fatto, setFatto] = useState(false);

  // Chiudere azzera il modulo: riaprendolo ci si ritrovava il testo di prima,
  // che sembra un errore anche quando non lo è.
  const chiudi = () => { setAperto(false); setFatto(false); setForm(vuota()); };

  const invia = async () => {
    if (!form.titolo.trim()) return alert('Scrivi una riga che riassuma la segnalazione');
    setInviando(true);
    try {
      await segnalazioniApi.crea({
        ...form,
        pagina: window.location.pathname + window.location.search,
        versione: import.meta.env.VITE_BUILD_ID || 'sviluppo',
        browser: browserBreve(),
      });
      setFatto(true);
      setForm(vuota());
      setTimeout(chiudi, 1800);
    } catch (e) { alert(e); } finally { setInviando(false); }
  };

  return (
    <>
      <button
        onClick={() => { setFatto(false); setAperto(true); }}
        className="w-full text-left text-xs text-brand-100/80 hover:text-white px-2 py-1 rounded hover:bg-white/10"
        title="Segnala un problema o proponi una modifica">
        💬 Segnala un problema
      </button>

      {aperto && (
        <Modal title="Segnalazione" onClose={chiudi} size="lg">
          {fatto ? (
            <div className="py-8 text-center">
              <div className="text-3xl mb-2">✓</div>
              <p className="text-gray-700">Grazie, la segnalazione è arrivata.</p>
              <p className="text-xs text-gray-500 mt-1">La vedrà Fabio insieme alle altre.</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                <div className="flex gap-2">
                  {[['bug', '🐞 Qualcosa non funziona'], ['proposta', '💡 Proposta di modifica']].map(([v, l]) => (
                    <button key={v} onClick={() => setForm(f => ({ ...f, tipo: v }))}
                      className={`btn-sm btn flex-1 ${form.tipo === v
                        ? 'bg-brand-600 text-white'
                        : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
                      {l}
                    </button>
                  ))}
                </div>

                <div>
                  <label className="label">In una riga *</label>
                  <input className="input" autoFocus maxLength={200}
                         placeholder={form.tipo === 'bug'
                           ? 'es. Creando un SAL a volte non si apre la scheda'
                           : 'es. Poter filtrare le commesse per consulente'}
                         value={form.titolo}
                         onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))} />
                </div>

                <div>
                  <label className="label">Dettagli</label>
                  <textarea className="input" rows={5}
                            placeholder={form.tipo === 'bug'
                              ? 'Cosa stavi facendo, cosa ti aspettavi e cosa è successo invece.'
                              : 'A cosa ti servirebbe e in quale schermata.'}
                            value={form.descrizione}
                            onChange={e => setForm(f => ({ ...f, descrizione: e.target.value }))} />
                </div>

                {/* Dichiarato apertamente: nessuno deve scoprire dopo cosa è stato inviato */}
                <p className="text-xs text-gray-400">
                  Insieme al testo vengono registrati la schermata da cui scrivi, la versione
                  del gestionale e il browser: servono a capire il problema senza doverti
                  richiamare. Nient'altro.
                </p>
              </div>

              <div className="flex justify-end gap-2 mt-5">
                <button className="btn-secondary" onClick={chiudi}>Annulla</button>
                <button className="btn-primary" onClick={invia} disabled={inviando}>
                  {inviando ? 'Invio…' : 'Invia segnalazione'}
                </button>
              </div>
            </>
          )}
        </Modal>
      )}
    </>
  );
}
